import { Injectable } from "@angular/core";
import { SortByAttribute } from "@hsgovtech/hsutils";
import { BehaviorSubject } from "rxjs";
import { InspectionViolationRepository } from "../repositories/inspection.violation.repository";
import { IrefItemObservationRepository } from "../repositories/iref.item.obs.repository";
import { InspectionViolation } from "../types/Violation/InspectionViolation";
import { ViolationObservation } from "../types/Violation/ViolationObservation";
import { ClientService } from "./client.service";
import { LogService } from "./log.service";
import { BaseService } from "./base.service";
import { LocationHistory } from "../types/Location/LocationHistory";
import { LocationHistoryItem } from "../types/Location/LocationHistoryItem";
import { ViolationItem } from "../types/Violation/ViolationItem";
import { IrefItemCodeRepository } from "../repositories/iref.item.code.repository";

@Injectable({
  providedIn: "root",
})
export class ObservationService extends BaseService {
  observationMapSubject: BehaviorSubject<{ [key: string]: string[] }> =
    new BehaviorSubject({});

  observationMap$ = this.observationMapSubject.asObservable();

  client: any;

  constructor(
    private clientService: ClientService,
    private irefItemObservationRepo: IrefItemObservationRepository,
    private inspectionViolationRepo: InspectionViolationRepository,
    private inspectionViolationCodeRepo: IrefItemCodeRepository,
    logService: LogService,
  ) {
    super(logService, "ObservationService");
    this.client = this.clientService.getCurrentClient();
  }

  public getInspectionViolationsByItemID(
    locationID: string,
    inspectionID: string,
    itemID: string,
  ) {
    return this.inspectionViolationRepo.getInspectionViolationsByItemID(
      locationID,
      inspectionID,
      itemID,
    );
  }

  public setObservationMap(locationID: string, inspectionID: string) {
    const violations =
      this.inspectionViolationRepo.getInspectionViolationsByInspectionID(
        locationID,
        inspectionID,
      );
    const newObservationMap = violations.reduce(
      (acc: any, violation: InspectionViolation) => {
        const observation = this.irefItemObservationRepo.getById(
          violation.observationID,
        );
        if (acc[violation.itemID]) {
          return {
            ...acc,
            [violation.itemID]: [
              ...acc[violation.itemID],
              observation.observationID,
            ],
          };
        }
        return {
          ...acc,
          [violation.itemID]: [observation.observationID],
        };
      },
      {} as any,
    );

    // Notify subscribers of the change
    this.observationMapSubject.next(newObservationMap);
  }

  public getIrefItemObservationByObservationID(
    observationID: string,
  ): ViolationObservation {
    return this.irefItemObservationRepo.getById(observationID);
  }

  private getObservationsFromViolations(
    item: ViolationItem,
    violations: any,
    observations: ViolationObservation[],
  ): any {
    return violations.reduce((acc: any, violation: any) => {
      const observation = observations.find(
        (obs: ViolationObservation) =>
          obs.observationID === violation.observationID,
      );
      if (observation?.itemID === item.itemID) {
        return [...acc, { ...violation, observationRec: observation }];
      }
      return acc;
    }, []);
  }

  public getObservations() {
    return this.irefItemObservationRepo.get();
  }

  public getObservationInfo(
    items: ViolationItem[],
    violations: InspectionViolation[],
    observations: ViolationObservation[],
    violationItemCodes: any[],
    filteredItems: any[],
  ) {
    const itemsToShow =
      filteredItems && filteredItems.length ? filteredItems : items;

    return itemsToShow.map((item: ViolationItem) => {
      // if sort order exists use it, if not sort alphabetically by observation
      const filteredObservations = observations.filter(
        (obs: any) => obs.itemID === item.itemID,
      );

      let finalObservations = [...(item.observations || [])];
      if (filteredObservations.every((obs: any) => obs.sort)) {
        finalObservations = SortByAttribute(filteredObservations, "sort");
      } else {
        finalObservations = SortByAttribute(
          filteredObservations,
          "observation",
        );
      }

      const filterVioCodes = violationItemCodes.filter(
        (code: any) => code.itemID === item.itemID,
      );

      if (this.getObservationFromMap(item.itemID)) {
        const newItem = {
          ...item,
          completed: true,
          observations: finalObservations,
          vioCodes: filterVioCodes,
        };
        return {
          ...newItem,
          violations: this.getObservationsFromViolations(
            newItem,
            violations,
            observations,
          ),
        };
      }
      return {
        ...item,
        completed: false,
        vioCodes: filterVioCodes,
        observations: finalObservations,
        violations: [],
      };
    });
  }

  public getObservationFromMap(itemID: string) {
    return this.observationMapSubject.value[itemID];
  }

  public getObservationsFromAllPreviousInspections(
    fullHistory: LocationHistory,
    isViolationOnly: boolean = false,
  ): any {
    return Object.values(fullHistory).reduce(
      (acc: any, curr: LocationHistoryItem) => {
        Object.keys(curr.observations).forEach((key) => {
          const currentObservations: any = isViolationOnly
            ? curr.observations[key].filter((o: any) => o.isViolation)
            : curr.observations[key];

          if (!acc[key]) {
            acc[key] = [];
          }
          const observationsWithPurpose = currentObservations.map(
            (observation: any) => {
              const vioCode = this.inspectionViolationCodeRepo.getById(
                observation.codeID,
              );
              return {
                ...observation,
                purpose: curr.purpose || "",
                code: vioCode?.code || "",
              };
            },
          );
          acc[key] = [...acc[key], ...observationsWithPurpose];
        });
        return acc;
      },
      {},
    );
  }
}
